import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, Shield, Users, FileText, Calendar, AlertTriangle, 
  CheckCircle, Clock, Download, Upload, Lock, Eye, EyeOff,
  Search, Filter, Plus, Edit, Building
} from "lucide-react";
import { format, parseISO, addDays } from "date-fns";
import { useQuery } from "@tanstack/react-query";

export default function HIPAADetails() {
  const [selectedTab, setSelectedTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");

  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ["/api/employees"],
  });

  // Mock HIPAA compliance data
  const [baAgreements] = useState([
    {
      id: "1",
      vendor: "Claims Processing Solutions",
      type: "Claims Administration",
      signedDate: "2024-01-15",
      expirationDate: "2025-01-14",
      status: "active",
      lastReview: "2024-06-15",
      riskLevel: "low"
    },
    {
      id: "2",
      vendor: "HealthTech Analytics",
      type: "Data Analytics",
      signedDate: "2024-03-01",
      expirationDate: "2025-02-28",
      status: "active",
      lastReview: "2024-09-01",
      riskLevel: "medium"
    },
    {
      id: "3",
      vendor: "SecureCloud Storage",
      type: "Data Storage",
      signedDate: "2023-12-01",
      expirationDate: "2024-11-30",
      status: "expiring-soon",
      lastReview: "2024-03-01",
      riskLevel: "high"
    }
  ]);

  const [privacyIncidents] = useState([
    {
      id: "1",
      type: "Unauthorized Access",
      description: "Employee accessed records outside scope of duties",
      reportedDate: "2024-05-20",
      status: "resolved",
      severity: "medium",
      affectedRecords: 5,
      reportedBy: "Privacy Officer",
      resolution: "Additional training provided, access permissions updated"
    },
    {
      id: "2",
      type: "Data Breach",
      description: "Email containing PHI sent to wrong recipient",
      reportedDate: "2024-04-10",
      status: "resolved",
      severity: "low",
      affectedRecords: 1,
      reportedBy: "HR Manager",
      resolution: "Recipient confirmed deletion, policy reinforcement conducted"
    }
  ]);

  // Calculate metrics
  const activeBAAs = baAgreements.filter(ba => ba.status === 'active').length;
  const expiringBAAs = baAgreements.filter(ba => ba.status === 'expiring-soon').length;
  const totalIncidents = privacyIncidents.length;
  const complianceScore = 92; // Mock calculation

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default">Active</Badge>;
      case 'expiring-soon':
        return <Badge variant="destructive">Expiring Soon</Badge>;
      case 'expired':
        return <Badge variant="secondary">Expired</Badge>;
      case 'resolved':
        return <Badge variant="default" className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Resolved</Badge>;
      case 'investigating':
        return <Badge variant="secondary">Investigating</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getRiskBadge = (risk: string) => {
    switch (risk) {
      case 'low':
        return <Badge variant="default" className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Low Risk</Badge>;
      case 'medium':
        return <Badge variant="secondary">Medium Risk</Badge>;
      case 'high':
        return <Badge variant="destructive">High Risk</Badge>;
      default:
        return <Badge variant="outline">{risk}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.history.back()}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Client
          </Button>
          <h1 className="text-3xl font-bold">HIPAA Compliance Management</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add BA Agreement
          </Button>
        </div>
      </div>

      {/* Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Compliance Score</CardTitle>
            <Shield className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{complianceScore}%</div>
            <p className="text-xs text-muted-foreground">
              Overall HIPAA compliance
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active BA Agreements</CardTitle>
            <FileText className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{activeBAAs}</div>
            <p className="text-xs text-muted-foreground">
              Current business associates
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{expiringBAAs}</div>
            <p className="text-xs text-muted-foreground">
              Within 30 days
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Privacy Incidents</CardTitle>
            <Eye className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{totalIncidents}</div>
            <p className="text-xs text-muted-foreground">
              Last 12 months
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="policies">Policies</TabsTrigger>
          <TabsTrigger value="business-associates">Business Associates</TabsTrigger>
          <TabsTrigger value="incidents">Privacy Incidents</TabsTrigger>
          <TabsTrigger value="training">Training</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  HIPAA Compliance Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Privacy Rule</span>
                  <Badge variant="default">Compliant</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Security Rule</span>
                  <Badge variant="default">Compliant</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Breach Notification Rule</span>
                  <Badge variant="default">Compliant</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Enforcement Rule</span>
                  <Badge variant="default">Compliant</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Security Safeguards
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Administrative Safeguards</span>
                  <Badge variant="default">Implemented</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Physical Safeguards</span>
                  <Badge variant="default">Implemented</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Technical Safeguards</span>
                  <Badge variant="default">Implemented</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Risk Assessment</span>
                  <Badge variant="secondary">Due for Review</Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Compliance Scorecard</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{complianceScore}%</div>
                <p className="text-muted-foreground">Overall HIPAA Compliance Score</p>
                <Progress value={complianceScore} className="mt-4" />
              </div>

              <div className="grid gap-4 md:grid-cols-4">
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-green-600">18</div>
                  <p className="text-sm text-muted-foreground">Policies & Procedures</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{employees.length}</div>
                  <p className="text-sm text-muted-foreground">Trained Workforce</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">{activeBAAs}</div>
                  <p className="text-sm text-muted-foreground">BA Agreements</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">0</div>
                  <p className="text-sm text-muted-foreground">Open Incidents</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Policies Tab */}
        <TabsContent value="policies" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                HIPAA Policies & Procedures
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-3">
                  <h4 className="font-semibold">Privacy Rule Policies</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">Notice of Privacy Practices</span>
                      <Badge variant="default">Current</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">Patient Rights</span>
                      <Badge variant="default">Current</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">Uses and Disclosures</span>
                      <Badge variant="default">Current</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">Minimum Necessary</span>
                      <Badge variant="default">Current</Badge>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold">Security Rule Policies</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">Access Control</span>
                      <Badge variant="default">Implemented</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">Audit Controls</span>
                      <Badge variant="default">Implemented</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">Data Integrity</span>
                      <Badge variant="default">Implemented</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">Transmission Security</span>
                      <Badge variant="secondary">Review Needed</Badge>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Policy Review Schedule</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Last comprehensive review</span>
                    <span className="font-medium">January 2024</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Next scheduled review</span>
                    <span className="font-medium">January 2025</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Review frequency</span>
                    <span className="font-medium">Annual</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business Associates Tab */}
        <TabsContent value="business-associates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Business Associate Agreements ({baAgreements.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {baAgreements.map((ba) => (
                  <div key={ba.id} className="border rounded-lg p-4 hover:bg-muted/25 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-lg">{ba.vendor}</h3>
                          {getStatusBadge(ba.status)}
                          {getRiskBadge(ba.riskLevel)}
                        </div>
                        <p className="text-sm text-muted-foreground">{ba.type}</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4 mr-2" />
                          View Agreement
                        </Button>
                        <Button variant="outline" size="sm">
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 text-sm">
                      <div>
                        <span className="font-medium text-muted-foreground">Signed Date:</span>
                        <p className="font-semibold">{format(parseISO(ba.signedDate), 'MMM d, yyyy')}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Expiration:</span>
                        <p className="font-semibold">{format(parseISO(ba.expirationDate), 'MMM d, yyyy')}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Last Review:</span>
                        <p className="font-semibold">{format(parseISO(ba.lastReview), 'MMM d, yyyy')}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Days to Expiry:</span>
                        <p className="font-semibold">
                          {Math.max(0, Math.ceil((parseISO(ba.expirationDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))} days
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {baAgreements.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="mb-2">No business associate agreements on file.</p>
                    <p className="text-sm">Add agreements for vendors that handle PHI.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Privacy Incidents Tab */}
        <TabsContent value="incidents" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Privacy Incidents & Breaches
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {privacyIncidents.map((incident) => (
                  <div key={incident.id} className="border rounded-lg p-4 hover:bg-muted/25 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-lg">{incident.type}</h3>
                          {getStatusBadge(incident.status)}
                          <Badge variant={incident.severity === 'high' ? 'destructive' : incident.severity === 'medium' ? 'secondary' : 'outline'}>
                            {incident.severity} severity
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{incident.description}</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </Button>
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 text-sm">
                      <div>
                        <span className="font-medium text-muted-foreground">Reported Date:</span>
                        <p className="font-semibold">{format(parseISO(incident.reportedDate), 'MMM d, yyyy')}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Reported By:</span>
                        <p className="font-semibold">{incident.reportedBy}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Affected Records:</span>
                        <p className="font-semibold">{incident.affectedRecords}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Status:</span>
                        <p className="font-semibold capitalize">{incident.status}</p>
                      </div>
                    </div>

                    {incident.resolution && (
                      <div className="mt-3 p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded">
                        <span className="font-medium text-green-700 dark:text-green-300 text-sm">Resolution: </span>
                        <span className="text-sm text-green-600 dark:text-green-400">{incident.resolution}</span>
                      </div>
                    )}
                  </div>
                ))}

                {privacyIncidents.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="mb-2">No privacy incidents reported in the last 12 months.</p>
                    <p className="text-sm">This indicates strong privacy controls are in place.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Training Tab */}
        <TabsContent value="training" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                HIPAA Training Program
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{Math.round(employees.length * 0.95)}</div>
                  <p className="text-sm text-muted-foreground">Employees Trained</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">95%</div>
                  <p className="text-sm text-muted-foreground">Completion Rate</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">3</div>
                  <p className="text-sm text-muted-foreground">Overdue Training</p>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold">Training Requirements</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Initial HIPAA Training</p>
                      <p className="text-sm text-muted-foreground">Required for all new hires within 30 days</p>
                    </div>
                    <Badge variant="default">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Annual Refresher Training</p>
                      <p className="text-sm text-muted-foreground">Required annually for all workforce members</p>
                    </div>
                    <Badge variant="default">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Incident Response Training</p>
                      <p className="text-sm text-muted-foreground">Additional training after security incidents</p>
                    </div>
                    <Badge variant="secondary">As Needed</Badge>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Training Schedule</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Next training session</span>
                    <span className="font-medium">December 15, 2024</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Training method</span>
                    <span className="font-medium">Online modules + In-person review</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Completion deadline</span>
                    <span className="font-medium">January 31, 2025</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}